import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

import Gallery from "../../../../components/contract/gallery";
import { ECONTRACT_STATUS, EPARTY_STATUS } from "../../../../enums";
import {
  ESenderRole,
  ETrasactionsType,
  IContractDetailsResponseData,
  Transactions,
} from "../../../../setup/api/services/contract/models";
import { getBankTransferDetails } from "../../../../setup/api/services/payment";
import { findPartyForProfile } from "../../../../setup/util/contract";
import { PAYMENT_DETAILS } from "../../authenticatedContractDetails/constants";
import styles from "./styles.module.scss";

interface ITransferProof {
  transaction: Transactions;
  payerName?: string;
}

/** One bank transfer: its date and the proof documents uploaded with it. */
const TransferProof = ({ transaction, payerName }: ITransferProof) => {
  const { t } = useTranslation();
  const [images, setImages] = useState<string[]>([]);

  useEffect(() => {
    if (!transaction?.referenceTransactionId) return;
    getBankTransferDetails(transaction.referenceTransactionId)
      .then((data) => {
        if (data?.data?.docs) setImages(data?.data?.docs);
      })
      .catch((error) => console.error("error", error));
  }, [transaction?.referenceTransactionId]);

  return (
    <div className={styles.transfer}>
      {payerName && <p className={styles.payer}>{payerName}</p>}
      <p className={styles.date}>
        {moment(transaction?.createdAt || new Date())
          .locale("ar")
          .format(`DD MMMM YYYY - hh:mm`)}
      </p>
      {images.length > 0 && (
        <Gallery
          isBrokerContract
          header={t("lbl_attachment_proof")}
          images={images}
          style={{ marginTop: "0.5rem" }}
        />
      )}
    </div>
  );
};

interface IBankTransferProof {
  milestone: IContractDetailsResponseData;
}

/**
 * The default contract details' payment details section — the date of a
 * manual bank transfer and the proof uploaded with it, fetched from the
 * payment service — for every transfer awaiting approval on the milestone.
 *
 * The default page shows it while the contract is PENDING_PAYMENT_VERIFICATION.
 * A milestone waiting on a transfer is often PARTIALLY_PAID instead, its
 * customer marked PAYMENT_PENDING_APPROVAL, so that counts as waiting too.
 */
const BankTransferProof = ({ milestone }: IBankTransferProof) => {
  const isAwaitingApproval =
    milestone?.status === ECONTRACT_STATUS.PENDING_PAYMENT_VERIFICATION ||
    (milestone?.status === ECONTRACT_STATUS.PARTIALLY_PAID &&
      !!milestone?.parties?.some(
        (party) =>
          party?.role === ESenderRole.CUSTOMER &&
          party?.status === EPARTY_STATUS.PAYMENT_PENDING_APPROVAL
      ));

  const transfers = useMemo(() => {
    const pendingTransfers =
      milestone?.transactions?.filter(
        (transaction) =>
          transaction?.type === ETrasactionsType.MANUAL_BANK_TRANSFER &&
          transaction?.status === "PENDING"
      ) || [];

    // as on the default page, fall back to the contract's first transaction
    const shown = pendingTransfers.length
      ? pendingTransfers
      : milestone?.transactions?.[0]
      ? [milestone.transactions[0]]
      : [];

    const customers = milestone?.partiesProfiles?.CUSTOMER || [];
    return shown.map((transaction) => {
      const payer = customers.find((profile) => {
        const party = findPartyForProfile(
          milestone?.parties,
          profile,
          ESenderRole.CUSTOMER
        );
        return (
          !!transaction?.fromUserId &&
          [party?.userId, profile?.userIdentification, profile?.id]
            .filter((value) => value !== undefined && value !== null)
            .map(String)
            .includes(String(transaction.fromUserId))
        );
      });
      return { transaction, payerName: payer?.profile?.fullName };
    });
  }, [milestone?.transactions, milestone?.partiesProfiles, milestone?.parties]);

  if (!isAwaitingApproval || !transfers.length) return null;

  return (
    <div className={styles.container}>
      <h4 className={styles.title}>{PAYMENT_DETAILS}</h4>
      {transfers.map(({ transaction, payerName }, index) => (
        <TransferProof
          key={transaction?.referenceTransactionId || index}
          transaction={transaction}
          // name the payer only when there is more than one transfer to tell apart
          payerName={transfers.length > 1 ? payerName : undefined}
        />
      ))}
    </div>
  );
};

export default BankTransferProof;
